import React from 'react';
import PropTypes from 'prop-types';
import {
  LineChart as RLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  useChartTheme,
  ChartTooltip,
} from '../BarChart';
import styles from '../BarChart/Charts.module.scss';

/* ────────────────────────────────────────────────────────────
 * LineChart — themed Recharts wrapper.
 *
 *   <LineChart
 *     data={[{ day: 'Mon', orders: 120 }, ...]}
 *     xKey="day"
 *     series={[
 *       { key: 'orders', name: 'Orders' },
 *       { key: 'cancelled', name: 'Cancelled', color: 'var(--chart-5)' },
 *     ]}
 *     height={300}
 *   />
 *
 * Series colors auto-cycle through --chart-1 → --chart-8.
 * Honors MOTION.md §4.10 — bars/lines animate in over 400ms on
 * mount; filter swaps cross-fade instead.
 * ──────────────────────────────────────────────────────────── */

const LineChart = ({
  data,
  xKey,
  series,
  height,
  smooth,
  valueFormatter,
  labelFormatter,
  showLegend,
  showGrid,
  showTooltip,
  showDots,
  className,
  ariaLabel,
}) => {
  const palette = useChartTheme();

  return (
    <div
      className={[styles.chart, className || ''].filter(Boolean).join(' ')}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RLineChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} vertical={false} />
          )}
          <XAxis dataKey={xKey} stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
          <YAxis stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
          {showTooltip && (
            <Tooltip
              cursor={{ stroke: palette.grid, strokeWidth: 1 }}
              content={(p) => (
                <ChartTooltip
                  {...p}
                  valueFormatter={valueFormatter}
                  labelFormatter={labelFormatter}
                />
              )}
            />
          )}
          {showLegend && <Legend wrapperStyle={{ color: palette.axis, fontSize: 12 }} />}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type={smooth ? 'monotone' : 'linear'}
              dataKey={s.key}
              name={s.name || s.key}
              stroke={s.color || palette.series[i % palette.series.length]}
              strokeWidth={2}
              dot={showDots ? { r: 3, strokeWidth: 0 } : false}
              activeDot={{ r: 5, strokeWidth: 0 }}
              isAnimationActive
              animationDuration={400}
            />
          ))}
        </RLineChart>
      </ResponsiveContainer>
    </div>
  );
};

LineChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  xKey: PropTypes.string.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
    })
  ).isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  smooth: PropTypes.bool,
  valueFormatter: PropTypes.func,
  labelFormatter: PropTypes.func,
  showLegend: PropTypes.bool,
  showGrid: PropTypes.bool,
  showTooltip: PropTypes.bool,
  showDots: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

LineChart.defaultProps = {
  height: 320,
  smooth: true,
  valueFormatter: null,
  labelFormatter: null,
  showLegend: true,
  showGrid: true,
  showTooltip: true,
  showDots: false,
  className: '',
  ariaLabel: 'Line chart',
};

export default LineChart;
